import { useEffect, useState, FC, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogActions, DialogTitle, IconButton, Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import { normalizeString } from 'utils/Validations';
import { TimekeeperService } from 'services/TimekeeperService';
import { generateRow, FixedRule, InterpretationRule, WorkRule, HolidayRule, Holiday, PayRuleFormData } from './common';
import { HolidayTabs } from 'repository/timekeeper/@types/HolidayModel';
import { PayRulesForm } from './PayRulesForm';
import { PaperComponent } from 'shared/ui/draggable/Draggable';
import { AlertCustom } from 'shared/ui/alert/AlertCustom';

// Interfaces
interface AddPayRulesProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    rows: PayRule[];
    reloading: () => void;
    cleanSelected: () => void;
    mode: string;
}

interface PayRule {
    id: number;
    name: string;
}

const timekeeperService = new TimekeeperService();

// Main Component
const AddPayRules: FC<AddPayRulesProps> = ({ open, setOpen, rows, reloading, cleanSelected, mode }) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    // Tab state
    const [selectedTab, setSelectedTab] = useState(0);

    // Unified form data
    const [formData, setFormData] = useState<PayRuleFormData>({
        name: '',
        description: '',
        calculateProjectedTotals: false,
        tryPlannedTime: false,
        fixedRuleSelected: null,
        interpretationRuleSelected: null,
        workRuleSelected: null,
        creditHolidayRuleSelected: null,
        holidaysSelected: [],
        assignment: [generateRow(0)],
    });

    // Rule options
    const [fixedRules, setFixedRules] = useState<FixedRule[]>([]);
    const [interpretationRules, setInterpretationRules] = useState<InterpretationRule[]>([]);
    const [workRules, setWorkRules] = useState<WorkRule[]>([]);
    const [creditHolidayRules, setCreditHolidayRules] = useState<HolidayRule[]>([]);

    // Holidays
    const [holidays, setHolidays] = useState<Holiday[]>([]);
    const [loadingWorkRules, setLoadingWorkRules] = useState(false);

    // Alerts & Loadings
    const [alerts, setAlerts] = useState(false);
    const [loading, setLoading] = useState(false);

    //Errors
    const [showError, setShowError] = useState<{
        start_hour?: boolean;
        end_hour?: boolean;
        min_shift?: boolean;
        max_shift?: boolean;
    }>({});

    // validate time inputs
    useEffect(() => {
        const timeout = setTimeout(() => {
            const newErrors: { [key: string]: boolean } = {};
            const data = formData.assignment.reduce(
                (acc, row) => {
                    acc.start_hour = acc.start_hour || row.start_hour;
                    acc.end_hour = acc.end_hour || row.end_hour;
                    acc.min_shift = acc.min_shift || row.min_shift;
                    acc.max_shift = acc.max_shift || row.max_shift;
                    return acc;
                },
                { start_hour: '', end_hour: '', min_shift: '', max_shift: '' },
            );

            for (const [key, value] of Object.entries(data)) {
                if (value.length > 0 && value.length < 5) {
                    newErrors[key] = true;
                }
            }
            setShowError(newErrors);
        }, 500);

        return () => clearTimeout(timeout);
    }, [formData.assignment]);

    // Memoized values
    const existName = useCallback(
        (name: string) => {
            const normalizedInput = normalizeString(name);
            return rows.some((row) => normalizeString(row.name) === normalizedInput);
        },
        [rows],
    );

    const hasChanges = useMemo(() => {
        return formData.name !== '' || formData.description !== '';
    }, [formData.name, formData.description]);

    const isDisabled = useMemo(() => {
        return (
            formData.name === '' ||
            existName(formData.name) ||
            showError.start_hour ||
            showError.end_hour ||
            showError.min_shift ||
            showError.max_shift ||
            !formData.fixedRuleSelected ||
            !formData.interpretationRuleSelected ||
            !formData.workRuleSelected
        );
    }, [formData.name, formData.fixedRuleSelected, formData.interpretationRuleSelected, formData.workRuleSelected, existName, showError]);

    const closeForm = useCallback(() => {
        setFormData({
            name: '',
            description: '',
            calculateProjectedTotals: false,
            tryPlannedTime: false,
            fixedRuleSelected: null,
            interpretationRuleSelected: null,
            workRuleSelected: null,
            creditHolidayRuleSelected: null,
            holidaysSelected: [],
            assignment: [generateRow(0)],
        });
        setFixedRules([]);
        setInterpretationRules([]);
        setWorkRules([]);
        setCreditHolidayRules([]);
        setHolidays([]);
        setLoading(false);
        setSelectedTab(0);
        setAlerts(false);
        setOpen(false);
        cleanSelected();
    }, [setOpen, cleanSelected]);

    const handleClose = useCallback(() => {
        if (hasChanges) {
            setAlerts(true);
        } else {
            closeForm();
        }
    }, [hasChanges, closeForm]);

    const addPayRule = useCallback(async () => {
        setLoading(true);
        const orderedData = formData.assignment.map((row) => {
            const { id, ...data } = row;
            return { ...data, days_week: Array.from(row.days_week) };
        });

        const body = {
            name: formData.name,
            description: formData.description,
            interpretation_rule_id: formData.interpretationRuleSelected?.interpretation_rule_id || 0,
            credit_holiday_default_id: formData.creditHolidayRuleSelected?.holiday_rule_id,
            fixed_rule_id: formData.fixedRuleSelected?.fixed_rule_id,
            work_rule_default_id: formData.workRuleSelected?.work_rule_id || 0,
            calculate_projected_totals: formData.calculateProjectedTotals,
            treat_planned_hour: formData.tryPlannedTime,
            holidays: formData.holidaysSelected,
            asignation: orderedData.length === 1 && orderedData[0].pay_rule_id === null ? [] : orderedData,
        };
        try {
            await timekeeperService.payrules.createPayRule(body);
            enqueueSnackbar(t('settings.pay_rules.dialog.alerts.add.success'), { variant: 'success' });
            reloading();
            closeForm();
        } catch (error) {
            console.error('Error al crear la regla de pago:', error);
            enqueueSnackbar(t('settings.pay_rules.dialog.alerts.add.error'), { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [t, enqueueSnackbar, reloading, closeForm, formData]);

    // Effects
    useEffect(() => {
        if (!open) return;

        const fetchData = async () => {
            setLoadingWorkRules(true);
            try {
                const [fixedRulesData, interpretationRulesData, workRulesData, creditHolidayRulesData, holidaysData] = await Promise.allSettled([
                    timekeeperService.fixed_rule.getFixedRules(),
                    timekeeperService.interpretation_rules.getAll(),
                    timekeeperService.work_rules.getWorkRules(),
                    timekeeperService.credit_holiday_rule.getCreditHolidayRules(),
                    timekeeperService.holiday.getHolidays(),
                ]);

                if (fixedRulesData.status === 'fulfilled') {
                    const orderedFixedRules = fixedRulesData.value.sort((a: FixedRule, b: FixedRule) => a.name.localeCompare(b.name));
                    setFixedRules(orderedFixedRules || []);
                }
                if (interpretationRulesData.status === 'fulfilled') {
                    const orderedInterpretationRules = interpretationRulesData.value.sort((a: InterpretationRule, b: InterpretationRule) => a.name.localeCompare(b.name));
                    setInterpretationRules(orderedInterpretationRules || []);
                }
                if (workRulesData.status === 'fulfilled') {
                    const orderedWorkRules = workRulesData.value.sort((a: WorkRule, b: WorkRule) => a.name.localeCompare(b.name));
                    setWorkRules(orderedWorkRules || []);
                }
                if (creditHolidayRulesData.status === 'fulfilled') {
                    const orderedCreditHolidayRules = creditHolidayRulesData.value.sort((a: HolidayRule, b: HolidayRule) => a.name.localeCompare(b.name));
                    setCreditHolidayRules(orderedCreditHolidayRules || []);
                }
                if (holidaysData.status === 'fulfilled') {
                    const holidayRows: HolidayTabs[] = holidaysData.value.map((holiday: { holiday_id: number; name: string; country?: string }) => ({
                        id: holiday.holiday_id,
                        name: holiday.name,
                        option: false,
                        holiday_credit_rule: '',
                        country: holiday.country || '',
                    }));
                    const orderedHolidayRows = holidayRows.sort((a, b) => a.name.localeCompare(b.name));
                    setHolidays(orderedHolidayRows);
                }
            } catch (error) {
                console.error('Error fetching rules:', error);
            } finally {
                setLoadingWorkRules(false);
            }
        };

        fetchData();
    }, [open]);

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') return;
                handleClose();
            }}
            maxWidth={selectedTab === 0 ? 'sm' : selectedTab === 1 ? 'md' : 'xl'}
            fullWidth
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {t('settings.pay_rules.dialog.titles.add')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>

            <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>{alerts && <AlertCustom alerts={alerts} setAlerts={setAlerts} closeForm={closeForm} t={t} />}</Box>

            <DialogContent dividers>
                <PayRulesForm
                    formData={formData}
                    setFormData={setFormData}
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                    ruleOptions={{
                        fixedRules,
                        interpretationRules,
                        workRules,
                        creditHolidayRules,
                    }}
                    holidays={holidays}
                    loadingWorkRules={loadingWorkRules}
                    existName={existName}
                    mode={mode}
                />
            </DialogContent>

            <DialogActions>
                <Button variant="contained" color="error" onClick={handleClose}>
                    {t('general.buttons.cancel')}
                </Button>
                <Button variant="contained" color="primary" onClick={addPayRule} disabled={isDisabled || loading} loading={loading}>
                    {t('general.buttons.add')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export { AddPayRules };
